import React, { Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { useGLTF } from '@react-three/drei';
import { PRESENTATION, GREETING, REACTIONS, EMOTES } from '../avatar/tourScript';
import { createMotionPack } from '../avatar/motion';

const MODEL_URL = `${process.env.PUBLIC_URL || ''}/models/avatar.glb`;
const AUDIO_BASE = `${process.env.PUBLIC_URL || ''}/audio/tour`;
// Mismo asistente que el chat flotante del portafolio
const CHAT_URL = 'https://back-end-portafolio-swart.vercel.app/api/ai/chat';
const WALK_SPEED = 1.3; // m/s: velocidad de marcha del avatar
const VIEW_HEIGHT_M = 2.46; // alto en metros que abarca el lienzo (para pasar píxeles a metros)

const clamp = (v, a, b) => Math.min(b, Math.max(a, v));
const lerp = (a, b, t) => a + (b - a) * t;
const sleep = (ms) => new Promise((r) => setTimeout(r, ms));
const store = {
  get: (k) => { try { return window.sessionStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { window.sessionStorage.setItem(k, v); } catch { /* sin storage */ } },
};
// posición horizontal del avatar según el lado (deja libre el botón del asistente a la derecha)
const xFor = (side, sz) => (side === 'left' ? 12 : Math.max(0, sz.vw - sz.w - 90));

/**
 * ctrl (ref) es el puente entre la interfaz y la animación:
 * { walk, speed, dir, talk, speaking, thinking, gesture, cue, cueId, analyser, waveSignal }.
 * El movimiento lo hace el pack de src/avatar/motion (base + capas); acá solo se le dice qué hacer.
 */
function Avatar({ ctrl, pointer }) {
  const { scene } = useGLTF(MODEL_URL);
  const pack = useMemo(() => createMotionPack(scene), [scene]);
  const group = useRef();
  const buf = useMemo(() => new Uint8Array(256), []);
  const s = useRef({ face: 0, walkAmt: 0, talk: 0, base: 'idle', speed: 0, loop: null, cueId: 0, wave: 0, glance: null, nextGlance: 6 });

  useFrame((fs, delta) => {
    const dt = Math.min(delta, 0.05);
    const t = fs.clock.elapsedTime;
    const c = ctrl.current;
    const st = s.current;
    const m = pack.motion;

    // Base: reposo o caminar (con la velocidad pedida)
    const wantBase = c.walk ? 'walk' : 'idle';
    if (wantBase !== st.base) { m.setBase(wantBase, { speed: c.speed }, 0.4); st.base = wantBase; }
    else if (c.walk && c.speed !== st.speed) m.setParams({ speed: c.speed });
    st.speed = c.speed;

    // Gestos mientras habla (bucle), y luego el gesto puntual encima
    const wantLoop = c.speaking ? (c.gesture || 'talk_beat') : null;
    if (wantLoop !== st.loop) {
      if (st.loop) m.stop(st.loop, 0.5);
      if (wantLoop) m.play(wantLoop, { fadeIn: 0.5 });
      st.loop = wantLoop;
    }
    if (c.cueId !== st.cueId) { st.cueId = c.cueId; if (c.cue) m.play(c.cue); }
    if (c.waveSignal !== st.wave) { st.wave = c.waveSignal; m.play('wave'); }

    // Boca: el nivel sale del audio (si hay analizador) o del pulso de la voz sintética
    let talk = c.talk;
    if (c.analyser) {
      c.analyser.getByteTimeDomainData(buf);
      let sum = 0;
      for (let i = 0; i < buf.length; i++) { const v = (buf[i] - 128) / 128; sum += v * v; }
      talk = clamp(Math.sqrt(sum / buf.length) * 5, 0, 1);
    }
    st.talk = lerp(st.talk, talk, 0.35);
    m.setFace('jaw', st.talk);
    m.setFace('funnel', st.talk * 0.12 * (0.5 + 0.5 * Math.sin(t * 13)));

    // Mirada: piensa mirando hacia arriba; si no, sigue al puntero; si el puntero lleva rato quieto, mira a su alrededor
    st.walkAmt = lerp(st.walkAmt, c.walk ? 1 : 0, 0.12);
    const k = 1 - st.walkAmt * 0.6;
    const idleFor = (performance.now() - (pointer.current.t || 0)) / 1000;
    if (!c.walk && !c.speaking && !c.thinking && idleFor > 5 && t > st.nextGlance) {
      st.glance = { x: (Math.random() - 0.5) * 1.4, y: (Math.random() - 0.3) * 0.5, until: t + 1.5 + Math.random() * 2 };
      st.nextGlance = t + 5 + Math.random() * 9;
      if (Math.random() < 0.15) m.play('shrug');
    }
    if (c.thinking) m.look(-0.3, 0.65);
    else if (st.glance && t < st.glance.until && idleFor > 5) m.look(st.glance.x, st.glance.y);
    else m.look(-pointer.current.x * k, -pointer.current.y * k);

    // Gira hacia donde camina
    st.face = lerp(st.face, c.walk ? c.dir * 1.1 : 0, 1 - Math.exp(-dt * 3));
    if (group.current) group.current.rotation.y = st.face;

    pack.update(dt);
  });

  return <group ref={group} position={[0, -0.95, 0]}><primitive object={scene} /></group>;
}

// --- Voz: audio pregrabado (voz clonada) si existe; si no, la voz sintética del navegador ---
async function hasAudio(id) {
  try {
    const r = await fetch(`${AUDIO_BASE}/${id}.mp3`, { method: 'HEAD', cache: 'no-store' });
    return r.ok && /audio/i.test(r.headers.get('content-type') || '');
  } catch { return false; }
}

function pickVoice() {
  const voices = window.speechSynthesis?.getVoices?.() || [];
  const es = voices.filter((v) => /^es/i.test(v.lang));
  // El avatar es masculino: se prefiere una voz masculina (Pablo, Jorge, Diego, Raúl…) y, si no hay, cualquiera en español
  const male = (v) => /pablo|jorge|diego|raul|raúl|alvaro|álvaro|carlos|miguel|juan|male/i.test(v.name);
  return es.find((v) => male(v) && /AR|419|MX|US/i.test(v.lang)) || es.find(male)
    || es.find((v) => /AR|419|MX|US/i.test(v.lang)) || es.find((v) => /natural|google/i.test(v.name)) || es[0] || null;
}

const splitSentences = (text) => text.match(/[^.!?¡¿]+[.!?]?/g)?.map((x) => x.trim()).filter(Boolean) || [text];
/** Texto apto para leer en voz alta: sin markdown, enlaces ni emojis, y acotado. */
const speakable = (t) => String(t).replace(/https?:\/\/\S+/g, '').replace(/[*_`#>~]/g, '')
  .replace(/[\u{1F300}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE0F}]/gu, '').replace(/\s+/g, ' ').trim().slice(0, 700);

export default function AvatarGuide() {
  const ctrl = useRef({ walk: false, speed: WALK_SPEED, dir: 1, talk: 0, speaking: false, thinking: false, gesture: null, cue: null, cueId: 0, analyser: null, waveSignal: 0 });
  const pointer = useRef({ x: 0, y: 0, t: 0 });
  const runId = useRef(0);
  const audioCtx = useRef(null);
  const audioEl = useRef(null);
  const wrapRef = useRef(null);
  const listRef = useRef(null);
  const bubbleTimer = useRef(null);
  const sessionId = useRef(typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : String(Date.now()));
  const [mounted, setMounted] = useState(false);
  const [side, setSide] = useState('left');
  const [panel, setPanel] = useState('intro'); // intro | chat | none
  const [bubble, setBubble] = useState('');
  const [mode, setMode] = useState('idle'); // idle | presenting | minimized
  const [busy, setBusy] = useState('idle'); // idle | thinking | speaking
  const [muted, setMuted] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: '¡Hola! Soy Tincho. Preguntame sobre mis servicios, mi experiencia o lo que quieras.', id: 0 }]);
  const [input, setInput] = useState('');
  const [size, setSize] = useState({ w: 300, h: 440, vw: 1200 });
  const [moveMs, setMoveMs] = useState(1500);
  const sizeRef = useRef(size);
  const sideRef = useRef('left');
  const mutedRef = useRef(false);
  useEffect(() => { sizeRef.current = size; }, [size]);
  useEffect(() => { mutedRef.current = muted; if (audioEl.current) audioEl.current.muted = muted; }, [muted]);
  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages, busy, panel]);

  // Montaje diferido: el modelo (~15 MB) se baja un rato después de cargar la página, sin bloquear
  useEffect(() => {
    if (store.get('avatar-min') === '1') { setMode('minimized'); return undefined; }
    const timer = setTimeout(() => { setMounted(true); ctrl.current.waveSignal += 1; }, 2500);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    const onResize = () => {
      const vw = window.innerWidth;
      const w = vw < 640 ? 170 : 300;
      setSize({ w, h: Math.round(w * 1.47), vw });
    };
    onResize();
    window.addEventListener('resize', onResize);
    const onMove = (e) => {
      pointer.current.t = performance.now();
      const r = wrapRef.current?.getBoundingClientRect();
      if (!r) return;
      pointer.current.x = clamp(((e.clientX - (r.left + r.width / 2)) / window.innerWidth) * 2.4, -1, 1);
      pointer.current.y = clamp(((e.clientY - (r.top + r.height * 0.3)) / window.innerHeight) * 2.4, -1, 1);
    };
    window.addEventListener('pointermove', onMove, { passive: true });
    return () => { window.removeEventListener('resize', onResize); window.removeEventListener('pointermove', onMove); clearTimeout(bubbleTimer.current); };
  }, []);

  /** Corta lo que esté diciendo o haciendo. */
  const stop = useCallback(() => {
    runId.current += 1;
    window.speechSynthesis?.cancel();
    if (audioEl.current) { audioEl.current.pause(); audioEl.current = null; }
    ctrl.current.analyser = null;
    ctrl.current.talk = 0;
    ctrl.current.speaking = false;
    ctrl.current.thinking = false;
    ctrl.current.walk = false;
  }, []);

  useEffect(() => stop, [stop]);

  const cue = (name) => { ctrl.current.cue = name; ctrl.current.cueId += 1; };

  const speakTTS = useCallback((text, id) => new Promise((resolve) => {
    const synth = window.speechSynthesis;
    if (!synth || mutedRef.current) {
      // sin voz: solo texto, con tiempo de lectura
      ctrl.current.speaking = true;
      const iv = setInterval(() => { ctrl.current.talk = 0.2 + Math.random() * 0.5; }, 130);
      setTimeout(() => { clearInterval(iv); if (id === runId.current) { ctrl.current.talk = 0; ctrl.current.speaking = false; } resolve(); }, Math.max(2500, text.length * 55));
      return;
    }
    const voice = pickVoice();
    const sentences = splitSentences(text);
    let i = 0;
    ctrl.current.speaking = true;
    // Mientras habla, la boca "late" aunque el motor no emita eventos de palabra
    const iv = setInterval(() => { if (ctrl.current.speaking && id === runId.current) ctrl.current.talk = Math.max(ctrl.current.talk * 0.6, Math.random() * 0.5); }, 140);
    const finish = () => { clearInterval(iv); resolve(); };
    const next = () => {
      if (id !== runId.current) { finish(); return; }
      if (i >= sentences.length) { ctrl.current.talk = 0; ctrl.current.speaking = false; finish(); return; }
      const u = new SpeechSynthesisUtterance(sentences[i++]);
      u.lang = voice?.lang || 'es-AR';
      if (voice) u.voice = voice;
      u.rate = 1.03; u.pitch = 1.05;
      u.onboundary = () => { ctrl.current.talk = 0.35 + Math.random() * 0.55; };
      u.onend = () => { ctrl.current.talk = 0; setTimeout(next, 120); };
      u.onerror = () => { ctrl.current.talk = 0; next(); };
      synth.speak(u);
    };
    next();
  }), []);

  const speakFile = useCallback((stepId, runToken) => new Promise((resolve) => {
    const audio = new Audio(`${AUDIO_BASE}/${stepId}.mp3`);
    audio.muted = mutedRef.current;
    audioEl.current = audio;
    try {
      if (!audioCtx.current) audioCtx.current = new (window.AudioContext || window.webkitAudioContext)();
      const src = audioCtx.current.createMediaElementSource(audio);
      const an = audioCtx.current.createAnalyser();
      an.fftSize = 512;
      src.connect(an); an.connect(audioCtx.current.destination);
      ctrl.current.analyser = an;
    } catch { ctrl.current.analyser = null; }
    ctrl.current.speaking = true;
    const done = () => { ctrl.current.analyser = null; ctrl.current.speaking = false; resolve(); };
    audio.onended = done; audio.onerror = done;
    audio.play().catch(done);
    const guard = setInterval(() => { if (runToken !== runId.current) { clearInterval(guard); audio.pause(); done(); } }, 200);
    audio.addEventListener('ended', () => clearInterval(guard));
  }), []);

  /** Dice un texto con voz y gestos. Resuelve cuando termina (o lo cortan). */
  const say = useCallback(async (text, { gesture = 'talk_beat', cueName = null } = {}) => {
    stop();
    const token = runId.current;
    ctrl.current.gesture = gesture;
    const speech = speakTTS(text, token); // marca speaking = true al instante
    if (cueName) cue(cueName);
    await speech;
  }, [speakTTS, stop]);

  // Camina de un lado al otro: la duración sale de la distancia y de la velocidad de marcha, para que los pies no patinen
  const walkTo = useCallback(async (nextSide) => {
    const from = sideRef.current;
    if (from === nextSide) return;
    const sz = sizeRef.current;
    const meters = Math.abs(xFor(nextSide, sz) - xFor(from, sz)) / (sz.h / VIEW_HEIGHT_M);
    const ms = clamp((meters / WALK_SPEED) * 1000, 900, 9000);
    sideRef.current = nextSide;
    setMoveMs(ms);
    ctrl.current.dir = nextSide === 'right' ? 1 : -1;
    ctrl.current.speed = WALK_SPEED;
    ctrl.current.walk = true;
    setSide(nextSide);
    await sleep(ms + 80);
    ctrl.current.walk = false;
  }, []);

  // --- Acciones ---
  const present = useCallback(async () => {
    stop();
    const token = runId.current;
    setPanel('none');
    setMode('presenting');
    if (audioCtx.current?.state === 'suspended') audioCtx.current.resume();
    for (const step of PRESENTATION) {
      if (token !== runId.current) return;
      setBubble(step.text);
      const file = !mutedRef.current && await hasAudio(step.id);
      if (token !== runId.current) return;
      ctrl.current.gesture = step.gesture || 'talk_beat';
      const speech = file ? speakFile(step.id, token) : speakTTS(step.text, token); // marca speaking = true al instante
      if (step.cue) cue(step.cue);
      await speech;
      if (token !== runId.current) return;
      await sleep(500);
    }
    if (token !== runId.current) return;
    setBubble('¡Eso es todo! Si querés charlar, tocá 💬.');
    setMode('idle');
  }, [speakFile, speakTTS, stop]);

  const stroll = useCallback(async () => {
    stop();
    const token = runId.current;
    const origin = sideRef.current;
    await walkTo(origin === 'left' ? 'right' : 'left');
    if (token !== runId.current) return;
    cue('wave');
    await sleep(2200);
    if (token !== runId.current) return;
    await walkTo(origin);
  }, [stop, walkTo]);

  const showQuip = (text, ms = 3500) => {
    setBubble(text);
    clearTimeout(bubbleTimer.current);
    bubbleTimer.current = setTimeout(() => setBubble(''), ms);
  };

  const react = () => {
    if (busy !== 'idle' || mode === 'presenting' || ctrl.current.walk) return;
    const r = REACTIONS[Math.floor(Math.random() * REACTIONS.length)];
    cue(r.cue);
    if (panel === 'none') showQuip(r.text);
  };

  const send = useCallback(async (raw) => {
    const msg = String(raw ?? '').trim().slice(0, 300);
    if (!msg || busy !== 'idle') return;
    stop();
    setInput('');
    setMode('idle');
    setBubble('');
    setMessages((m) => [...m, { role: 'user', content: msg, id: Date.now() }]);
    setBusy('thinking');
    ctrl.current.thinking = true;
    let reply;
    try {
      const r = await fetch(CHAT_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ message: msg, sessionId: sessionId.current }) });
      const data = await r.json();
      if (!r.ok || typeof data.response !== 'string') throw new Error('respuesta inválida');
      reply = data.response;
    } catch {
      ctrl.current.thinking = false;
      setMessages((m) => [...m, { role: 'assistant', content: '⚠️ No pude conectarme con el servidor. Probá de nuevo en un momento.', id: Date.now() + 1 }]);
      cue('shrug');
      setBusy('idle');
      return;
    }
    ctrl.current.thinking = false;
    setMessages((m) => [...m, { role: 'assistant', content: reply, id: Date.now() + 1 }]);
    setBusy('speaking');
    const greeting = /^\s*(¡?hola|buen[oa]s|hey)/i.test(reply);
    const sorry = /\b(no puedo|no tengo|lo siento|no sé)\b/i.test(reply);
    await say(speakable(reply), {
      gesture: Math.random() < 0.5 ? 'talk_open' : 'talk_beat',
      cueName: greeting ? 'wave' : sorry ? 'shrug' : 'nod_yes',
    });
    setBusy('idle');
  }, [busy, say, stop]);

  const quiet = () => { stop(); setBusy('idle'); setMode('idle'); setBubble(''); };
  const minimize = () => { stop(); setBusy('idle'); setMode('minimized'); setBubble(''); store.set('avatar-min', '1'); };
  const reopen = () => { store.set('avatar-min', '0'); setMounted(true); setMode('idle'); setPanel('chat'); ctrl.current.waveSignal += 1; };

  const btn = {
    background: 'rgba(255,69,0,0.95)', color: '#fff', border: 0, borderRadius: 999, padding: '7px 14px',
    fontSize: 13, fontWeight: 700, cursor: 'pointer', pointerEvents: 'auto',
  };
  const ghost = { ...btn, background: 'rgba(30,30,30,0.9)', border: '1px solid rgba(255,255,255,0.18)', color: '#eee', fontWeight: 600 };
  const icon = { ...ghost, padding: '6px 10px', fontSize: 15, lineHeight: 1 };

  if (mode === 'minimized') {
    return (
      <button type="button" onClick={reopen} aria-label="Mostrar a Tincho 3D" style={{ ...btn, position: 'fixed', left: 16, bottom: 16, zIndex: 900 }}>
        👋 Tincho 3D
      </button>
    );
  }
  if (!mounted) return null;

  const x = xFor(side, size);
  const panelW = Math.min(size.vw - 24, 330);
  const anchor = { position: 'absolute', zIndex: 3, bottom: size.h + 38, left: side === 'left' ? 0 : 'auto', right: side === 'right' ? 0 : 'auto', width: panelW, pointerEvents: 'auto' };
  const card = { background: 'rgba(14,14,14,0.95)', border: '1px solid rgba(255,69,0,0.45)', borderRadius: 16, color: '#f2f2f2', fontSize: 14, lineHeight: 1.5, boxShadow: '0 8px 30px rgba(0,0,0,0.5)' };

  return (
    <div
      ref={wrapRef}
      style={{
        position: 'fixed', left: 0, bottom: 0, width: size.w, zIndex: 900, pointerEvents: 'none',
        transform: `translateX(${x}px)`, transition: `transform ${moveMs}ms cubic-bezier(.25,0,.75,1)`,
      }}
    >
      {panel === 'chat' && (
        <div style={{ ...anchor, ...card, padding: 10 }} role="dialog" aria-label="Chat con Tincho">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <strong style={{ fontSize: 13, color: '#ff7b47' }}>Charlá con Tincho</strong>
            <button type="button" onClick={() => setPanel('none')} aria-label="Cerrar chat" style={{ ...icon, padding: '2px 8px', fontSize: 13 }}>—</button>
          </div>
          <div ref={listRef} style={{ maxHeight: Math.min(190, size.h * 0.45), overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6, paddingRight: 2 }}>
            {messages.map((m) => (
              <div key={m.id} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%', padding: '6px 10px', borderRadius: 12, fontSize: 13, background: m.role === 'user' ? 'rgba(255,69,0,0.9)' : 'rgba(255,255,255,0.08)', color: '#fff', whiteSpace: 'pre-wrap' }}>
                {m.content}
              </div>
            ))}
            {busy === 'thinking' && <div style={{ alignSelf: 'flex-start', opacity: 0.7, fontSize: 13 }}>Pensando…</div>}
          </div>
          <div style={{ display: 'flex', gap: 5, margin: '8px 0', flexWrap: 'wrap' }}>
            {EMOTES.map((e) => <button key={e.cue} type="button" style={icon} title={e.label} aria-label={e.label} onClick={() => { if (!ctrl.current.walk) cue(e.cue); }}>{e.icon}</button>)}
            <button type="button" style={icon} title="Pasear" aria-label="Pasear" onClick={stroll}>🚶</button>
            <button type="button" style={icon} title="Que se presente" aria-label="Que se presente" onClick={present}>🎤</button>
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(input); }} style={{ display: 'flex', gap: 6 }}>
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              maxLength={300}
              placeholder="Escribile algo…"
              disabled={busy === 'thinking'}
              aria-label="Mensaje para Tincho"
              style={{ flex: 1, minWidth: 0, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.18)', borderRadius: 999, color: '#fff', padding: '7px 12px', fontSize: 13, outline: 'none' }}
            />
            <button type="submit" style={btn} disabled={busy === 'thinking' || !input.trim()}>Enviar</button>
          </form>
        </div>
      )}

      {panel === 'intro' && (
        <div style={{ ...anchor, ...card, padding: '12px 14px' }} role="status">
          {GREETING}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button type="button" style={btn} onClick={() => setPanel('chat')}>Charlar</button>
            <button type="button" style={ghost} onClick={present}>Que se presente</button>
            <button type="button" style={ghost} onClick={() => setPanel('none')}>Más tarde</button>
          </div>
        </div>
      )}

      {panel === 'none' && bubble && (
        <div role="status" aria-live="polite" style={{ ...anchor, ...card, padding: '10px 14px', maxHeight: '46vh', overflowY: 'auto' }}>{bubble}</div>
      )}

      <div
        style={{ width: size.w, height: size.h, pointerEvents: 'auto', cursor: 'pointer' }}
        onClick={react}
        title="Hacele clic"
      >
        <Canvas dpr={[1, 1.6]} camera={{ position: [0, 0.3, 4.6], fov: 30 }} gl={{ alpha: true, antialias: true }} onCreated={({ camera }) => camera.lookAt(0, 0.3, 0)}>
          <ambientLight intensity={0.9} />
          <hemisphereLight args={['#ffffff', '#3a2a22', 0.8]} />
          <directionalLight position={[2.5, 3, 3]} intensity={2.2} />
          <directionalLight position={[-3, 1.5, -2]} intensity={1.6} color="#ff6a2b" />
          <Suspense fallback={null}><Avatar ctrl={ctrl} pointer={pointer} /></Suspense>
        </Canvas>
      </div>

      <div style={{ display: 'flex', gap: 6, justifyContent: 'center', padding: '4px 0 12px', flexWrap: 'wrap' }}>
        {(mode === 'presenting' || busy === 'speaking') ? (
          <button type="button" style={ghost} onClick={quiet}>⏹ Callar</button>
        ) : (
          panel !== 'chat' && <button type="button" style={btn} onClick={() => setPanel('chat')}>💬 Charlar</button>
        )}
        <button type="button" style={ghost} onClick={() => setMuted((v) => !v)} aria-label={muted ? 'Activar voz' : 'Silenciar voz'}>{muted ? '🔇' : '🔊'}</button>
        <button type="button" style={ghost} onClick={minimize} aria-label="Ocultar a Tincho">✕</button>
      </div>
    </div>
  );
}
